/**
 * LoadingOverlay Component
 * 
 * Displays a timer (MM:SS) over node content during processing.
 * Shows elapsed time and estimated remaining time.
 * 
 * Features:
 * - Semi-transparent overlay (85% opacity)
 * - Large responsive timer display
 * - Estimated time display below timer
 * - Color matches node type (text=yellow, image=cyan, video=red)
 * - Smooth fade in/out animations
 * - Scales to fill container width
 */

import React, { memo, useState, useEffect, useRef, useCallback } from 'react';
import { ESTIMATED_DURATIONS } from '../../hooks/useProgressSimulation';
import { generationLifecycleService } from '../../services/GenerationLifecycleService';
import './LoadingOverlay.css';

// Type color map matching the design system
const TYPE_COLORS: Record<string, string> = {
  text: '#FED402',   // Yellow
  image: '#42C3D7',  // Cyan
  video: '#FF4B42',  // Red
};

const FALLBACK_COLOR = '#A2A2A2';

/**
 * Format seconds to MM:SS display
 */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Module-level start-time registry, keyed by nodeId.
 *
 * Why module-level instead of component state: ReactFlow virtualizes nodes
 * with `onlyRenderVisibleElements`, so a node that scrolls out of the
 * viewport gets unmounted and remounted when it scrolls back in. Component
 * state (useState / useRef) is wiped on unmount, which made the timer reset
 * to 00:00 every time you scrolled the canvas. The Map survives unmount
 * because it lives at the module level — same lifetime as the page itself.
 */
const PROCESSING_START_TIMES = new Map<string, number>();

interface LoadingOverlayProps {
  /** Whether the node is currently processing */
  isProcessing: boolean;
  /** Unique node ID — used to key the start-time registry across remounts */
  nodeId?: string;
  /** The output type of the node (determines color) - text, image, or video */
  outputType?: string;
  /** Optional custom estimated duration in seconds */
  estimatedDuration?: number;
  /** Optional callback when progress completes */
  onComplete?: () => void;
}

const LoadingOverlayComponent: React.FC<LoadingOverlayProps> = ({
  isProcessing,
  nodeId,
  outputType = 'text',
  estimatedDuration,
  onComplete,
}) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  // Per-instance fallback when nodeId isn't provided. Keeps existing call
  // sites working but doesn't survive ReactFlow virtualization unmounts.
  const fallbackStartRef = useRef<number>(0);
  const intervalRef = useRef<number | null>(null);
  const onCompleteRef = useRef(onComplete);
  const containerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<HTMLSpanElement>(null);

  // Keep onComplete ref updated
  onCompleteRef.current = onComplete;

  // Normalize outputType to a valid key
  const normalizedType = outputType as 'text' | 'image' | 'video';
  const estimatedTime = estimatedDuration ?? ESTIMATED_DURATIONS[normalizedType] ?? 30;

  // Read the start timestamp for this node, or stamp one if processing just
  // began. When the component remounts (e.g. after a scroll-driven unmount)
  // we recover the original stamp instead of resetting. Preference order:
  //   1. GenerationLifecycleService run registry — authoritative for the
  //     handler families already ported to it (image), and cleaned up by
  //     finish() even when the node completes off-screen.
  //   2. Local module Map — families not yet ported (video/text) stamp here.
  //   3. Per-instance ref — call sites without a nodeId (legacy behavior).
  const getStartTime = useCallback((): number => {
    if (nodeId) {
      const fromService = generationLifecycleService.getStartTime(nodeId);
      if (fromService !== undefined) return fromService;
      const existing = PROCESSING_START_TIMES.get(nodeId);
      if (existing !== undefined) return existing;
      const fresh = Date.now();
      PROCESSING_START_TIMES.set(nodeId, fresh);
      return fresh;
    }
    if (fallbackStartRef.current === 0) {
      fallbackStartRef.current = Date.now();
    }
    return fallbackStartRef.current;
  }, [nodeId]);

  // Timer logic
  useEffect(() => {
    if (isProcessing) {
      // Resume from the stored start time (or stamp a new one if first run).
      const startTime = getStartTime();
      setElapsedSeconds((Date.now() - startTime) / 1000);
      setIsAnimating(true);

      intervalRef.current = window.setInterval(() => {
        setElapsedSeconds((Date.now() - startTime) / 1000);
      }, 100); // Update every 100ms for smooth display
    } else if (isAnimating) {
      // Stop timer
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Forget the start time so the next run starts fresh.
      if (nodeId) PROCESSING_START_TIMES.delete(nodeId);
      else fallbackStartRef.current = 0;
      // Fade out animation
      setTimeout(() => {
        setIsAnimating(false);
        onCompleteRef.current?.();
      }, 500);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Do NOT clear PROCESSING_START_TIMES on unmount — virtualization
      // can unmount us mid-run, and we need the start time when we remount.
    };
  }, [isProcessing, isAnimating, getStartTime, nodeId]);
  
  // Scale timer to fill container width
  const updateFontSize = useCallback(() => {
    if (!containerRef.current || !timerRef.current) return;
    
    const containerWidth = containerRef.current.offsetWidth;
    const containerHeight = containerRef.current.offsetHeight;
    
    // Target: timer takes ~80% of container width
    // Start with a reasonable font size and adjust
    const targetWidth = containerWidth * 0.8;
    const maxHeight = containerHeight * 0.5; // Leave room for estimate text
    
    // Reset to measure natural size
    timerRef.current.style.fontSize = '48px';
    const naturalWidth = timerRef.current.offsetWidth;
    const naturalHeight = timerRef.current.offsetHeight;
    
    if (naturalWidth > 0 && naturalHeight > 0) {
      // Scale by width, but cap by height
      const scaleByWidth = targetWidth / naturalWidth;
      const scaledHeight = naturalHeight * scaleByWidth;
      
      let finalScale = scaleByWidth;
      if (scaledHeight > maxHeight) {
        finalScale = maxHeight / naturalHeight;
      }
      
      const fontSize = Math.max(48, Math.min(48 * finalScale, 300));
      timerRef.current.style.fontSize = `${fontSize}px`;
    }
  }, []);
  
  // Update font size on mount and resize
  useEffect(() => {
    if (isAnimating || isProcessing) {
      updateFontSize();
      
      const resizeObserver = new ResizeObserver(updateFontSize);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }
      
      return () => resizeObserver.disconnect();
    }
    return undefined;
  }, [isAnimating, isProcessing, updateFontSize]);
  
  // Don't render if not animating and not processing
  if (!isAnimating && !isProcessing) {
    return null;
  }
  
  const color = TYPE_COLORS[outputType] || FALLBACK_COLOR;
  const timerDisplay = formatTime(elapsedSeconds);
  const estimateDisplay = `~${formatTime(estimatedTime)} estimated`;
  
  return (
    <div 
      ref={containerRef}
      className={`loading-overlay ${isProcessing ? 'loading-overlay--active' : 'loading-overlay--completing'}`}
    >
      <div className="loading-overlay__content">
        <span 
          ref={timerRef}
          className="loading-overlay__timer"
          style={{ color }}
        >
          {timerDisplay}
        </span>
        <span 
          className="loading-overlay__estimate"
          style={{ color: FALLBACK_COLOR }}
        >
          {estimateDisplay}
        </span>
      </div>
    </div>
  );
};

/**
 * Memoized LoadingOverlay to prevent unnecessary re-renders
 */
export const LoadingOverlay = memo(LoadingOverlayComponent);

export default LoadingOverlay;
